// Accessible and Progressively Enhanced Accordion (collapsible content)
//
// USAGE
// <div class="accordion js-collapsibleContent">
//     <h3 class="accordion-hd" data-collapsible-hdg>
//         This is a heading
//     </h3>
//     <div id="target_{{ id }}" class="accordion-bd" data-collapsible-focus-control>
//         <div class="accordion-bd-inner">
//             <div class="c-userContent">
//                 Content goes here.
//             </div>
//         </div>
//     </div>
// </div>

export default class CollapsibleContentView {
    static defaults = {};

    constructor($element, options) {
        /**
         * A reference to the containing DOM element.
         *
         * @default null
         * @property element
         * @type {object}
         * @public
         */
        this.element = $element[0];

        /**
         * Tracks whether component is active (content expanded/visible)
         *
         * @default false
         * @property isActive
         * @type {bool}
         * @public
         */
        this.isActive = false;

        /**
         * Tracks whether CSS is currently transitioning
         *
         * @default false
         * @property isTransitioning
         * @type {bool}
         * @public
         */
        this.isTransitioning = false;

        this.options = Object.assign({}, this.constructor.defaults, options);

        this.init();
    }

    attr = {
        controls: 'aria-controls',
        expanded: 'aria-expanded',
        hidden: 'aria-hidden',
    }

    selector = {
        hdg: '[data-collapsible-hdg]',
        trigger: '[data-collapsible-trigger]',
        target: '[id^="target"]',
        focusControl: '[data-collapsible-focus-control]',
    }

    listener = {
        transitionend: [
            'webkitTransitionEnd',
            'msTransitionEnd',
            'transitionend',
        ],
    }

    /**
     * Initializes the UI Component View.
     *
     * Runs setupHandlers, createChildren and layout.
     * Exits early if it is already initialized.
     *
     * @method init
     * @returns {Object} CollapsibleContentView
     * @private
     */
    init() {
        this.createChildren()
            .layout()
            .enable();

        return this;
    }
    
    /**
     * Destroys the view.
     * Tears down any events, handlers, elements.
     * Should be called when the object should be left unused.
     *
     * @method destroy
     * @returns {Object} CollapsibleContentView
     * @public
     */
    destroy() {
        this.disable()
            .removeChildren();

        return this;
    }

    /**
     * Create any child objects or references to DOM elements.
     * Should only be run on initialization of the view.
     *
     * @method createChildren
     * @returns {Object} CollapsibleContentView
     * @private
     */
    createChildren() {
        this.hdg = this.element.querySelector(this.selector.hdg);
        this.hdg.innerHTML = this.createInteractiveMarkup();
        this.trigger = this.element.querySelector(this.selector.trigger);
        this.target = this.element.querySelector(this.selector.target);
        this.id = this.target.id;
        this.targetInner = this.target.firstElementChild;
        this.defaultFocusControl = this.element.querySelector(this.selector.focusControl);

        return this;
    }

    /**
     * Remove any child objects or references to DOM elements.
     *
     * @method removeChildren
     * @returns {Object} CollapsibleContentView
     * @public
     */
    removeChildren() {
        this.hdg = null;
        this.trigger = null;
        this.target = null;
        this.id = null;
        this.targetInner = null;
        this.defaultFocusControl = null;

        return this;
    }

    layout() {
        this.trigger.setAttribute(this.attr.controls, this.id);
        this.trigger.setAttribute(this.attr.expanded, 'false');
        this.target.setAttribute(this.attr.hidden, 'true');
        this.target.setAttribute('role', 'region');
        this.target.style.height = '0';
        this.target.style.overflow = 'hidden';
        this.defaultFocusControl.setAttribute('tabindex', '-1');

        return this;
    }

    createInteractiveMarkup() {
        const hdg = this.hdg.innerHTML;
        const btnMarkup = `
            <button type="button" class="accordion-hd-btn" aria-expanded="false" data-collapsible-trigger>
                <div>
                    <div>${hdg}</div>
                    <div class="accordion-hd-btn-icon"></div>
                </div>
            </button>`;

        return btnMarkup;
    }

    /**
     * Enables the component.
     * Performs any event binding to handlers.
     * Exits early if it is already enabled.
     *
     * @method enable
     * @returns {Object} CollapsibleContentView
     * @public
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }
        this.isEnabled = true;

        this.trigger.addEventListener('click', this.onTriggerClick);
        this.listener.transitionend.forEach(transition => {
            document.addEventListener(transition, this.onTransitionEnd);
        });

        return this;
    }

    /**
     * Disables the component.
     * Tears down any event binding to handlers.
     * Exits early if it is already disabled.
     *
     * @method disable
     * @returns {Object} CollapsibleContentView
     * @public
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }
        this.isEnabled = false;

        this.trigger.removeEventListener('click', this.onTriggerClick);
        this.listener.transitionend.forEach(transition => {
            document.removeEventListener(transition, this.onTransitionEnd);
        });

        return this;
    }

    /**
     * Toggles the component.
     *
     * @method toggle
     * @returns {Object} CollapsibleContentView
     * @public
     */
    toggle() {
        this.isTransitioning = true;

        this.isActive ? this.hide() : this.show();

        return this;
    }

    /**
     * Shows the component.
     * Sets aria-expanded state for accessibility
     *
     * @method show
     * @returns {Object} CollapsibleContentView
     * @public
     */
    show() {
        this.isActive = true;
        this.target.style.height = `${this.targetInner.offsetHeight}px`;
        this.target.setAttribute(this.attr.hidden, 'false');
        this.trigger.setAttribute(this.attr.expanded, 'true');
        setTimeout(() => {
            this.defaultFocusControl.focus(); 
        }, 250); // --DEFAULT_TRANSITION_LENGTH

        return this;
    }

    /**
     * Hides the component.
     * Sets aria-expanded state for accessibility
     *
     * @method hide
     * @returns {Object} CollapsibleContentView
     * @public
     */
    hide() {
        this.isActive = false;
        this.target.style.height = 0;
        this.target.setAttribute(this.attr.hidden, 'true');
        this.trigger.setAttribute(this.attr.expanded, 'false');

        return this;
    }

    /*
        EVENT HANDLERS
    */

    /**
     * Trigger click event
     * Exits early if CSS transition is still in progress
     *
     * @method onTriggerClick
     * @param {event} e
     * @public
     */
    onTriggerClick = e => {
        if (this.isTransitioning) {
            return;
        }

        this.toggle();
    }

    /**
     * CSS transition end event
     *
     * @method onTransitionEnd
     * @param {event} e
     * @public
     */
    onTransitionEnd = e => {
        this.isTransitioning = false;
    }
}
